/**
 * DryStep (ydm) cart-aware reward-progress element, the projection bar.
 *
 * THE TRACK MEASURES PAIRS. It used to measure cart dollars, with gifts at $140 and $190. The BOGO
 * offer awards the same gifts on quantity, so the two disagreed: buy-4 earns free fresheners but
 * pays $100 to $116, and the bar told that shopper they were short of a gift the offer had already
 * given them. Gift milestones are now tier TOTALS in pairs (7 and 13), because that is what the cart
 * physically holds. Free shipping is still a dollar threshold, so it renders on its own line under
 * the bar rather than as a fourth milestone: two units on one axis cannot be read honestly.
 *
 * GIFTS HAVE TWO MODES (`data-gift-mode`). "tier" is the birthday sale: a gift belongs to a tier and
 * sits on the track as that tier's milestone. "spend" is the evergreen offer from 23 September 2026:
 * a gift unlocks at a cart value ($150 fresheners, $200 shoehorn), cart-wide, whatever tier is
 * chosen. Dollars are the shipping line's unit, so in spend mode each gift is drawn by renderGifts()
 * as its own line under the track, in the same four states as shipping, and the tiers carry no gift
 * at all. The theme only describes the gifts; Kaching Cart is what adds them to the cart.
 *
 * Counting pairs is the interesting part, because /cart.js line items carry no tags and the `N-pack`
 * tag is the only trustworthy source. Two layers, most authoritative first:
 *
 *   B. a server-rendered {product_id -> pairs} map (snippets/ydm-pair-map.liquid). Fast,
 *      synchronous, capped at the 50 products Liquid will iterate.
 *   C. /products/{handle}.js on a map miss, memoised in sessionStorage. This is what keeps the bar
 *      correct for SKUs added after the page was cached, which matters while new packs are being
 *      built mid-sale.
 *
 * (Layer A, the Liquid `data-cart-pairs` seed, went on 25 September 2026: it gave one total for the
 * whole cart, and the bar now needs each line, because only some pairs pool. See cartBreakdown().)
 *
 * On any uncertainty it rounds DOWN, to 0 pairs. Under-counting says "you need 2 more" when the
 * shopper does not, and the bundle app still applies the discount at the cart. Over-counting
 * promises a free shoehorn that never arrives, which is a refund and a support ticket.
 *
 * Honesty rules enforced here: the solid fill only ever reflects pairs really in the cart; the
 * element frames everything as "with this pack" and never speaks in achieved ("unlocked") tense,
 * which is language that belongs to the cart drawer only.
 *
 * Config-driven: milestones, scale, gift names and every string arrive via data-attributes, derived
 * from the live offer (snippets/ydm-offer.liquid). No Ka-ching app surface is touched.
 *
 * Degrades gracefully: with no widget it falls back to a cart-only nudge, kept even on an empty
 * cart, since that shopper still benefits from learning the threshold. If the widget's classes
 * drift, the chip degrades to scrollIntoView. If a widget tier's wording cannot be resolved to a
 * pair count at all, the projection is suppressed rather than guessed.
 */
(function () {
  'use strict';

  // Ka-ching light-DOM selectors — CONFIRMED against the live widget on drystep.com.au
  // (2026-07-29). The real markup: rows are `.kaching-bundles__bar` (selected row carries
  // `--selected`), each holds a plain `<input type="radio">` (there is NO `.…__bar-radio`
  // class), the current applied price is `.kaching-bundles__bar-price`, the strikethrough
  // compare-at is `.kaching-bundles__bar-full-price`, and per-unit is `.kaching-bundles__bar-
  // subtitle` ("$21.60 ea."). The tier name is `.kaching-bundles__bar-title` ("5 Pack").
  var KC = {
    bar: '.kaching-bundles__bar',
    title: '.kaching-bundles__bar-title',
    price: '.kaching-bundles__bar-price',
    fullPrice: '.kaching-bundles__bar-full-price',
    radio: 'input[type="radio"]',
    selected: '.kaching-bundles__bar--selected',
    barGuess: '[class*="kaching-bundles__bar"]'
  };

  var cartCents = null; // shared across instances; refreshed from /cart.js
  var cartItems = null; // raw /cart.js items, for recounting pairs after a cart write
  var pairMap = null; // {product_id: pairs} parsed once from the server-rendered map
  var pairMapRaw = ''; // the text it was parsed from, so a section re-render can invalidate it
  var pairLookup = {}; // handle -> pairs, resolved via /products/{handle}.js on a map miss
  var pairPending = {}; // handles currently in flight, so we never fire the same fetch twice

  // Once true, the upgrade chip's reserved slot is allowed to collapse when the chip is off
  // (removes the dead space in top-rung / no-upgrade states). It stays FALSE until either the
  // shopper interacts with the widget/cart (a user-initiated change — excluded from CLS) or we
  // confirm no bundle widget will load (a no-widget PDP). Before that, the slot is reserved from
  // first paint so an async widget-load bringing the chip in causes zero layout shift.
  var collapseOk = false;

  function clsOf(el) {
    if (!el || !el.className) return '';
    return typeof el.className === 'string' ? el.className : (el.className.baseVal || '');
  }

  function parseMoney(text) {
    if (!text) return null;
    var m = String(text).replace(/,/g, '').match(/\$?\s*(\d+(?:\.\d{1,2})?)/);
    if (!m) return null;
    return Math.round(parseFloat(m[1]) * 100);
  }

  function fmt(cents) {
    var d = cents / 100;
    return '$' + (d % 1 === 0 ? d.toFixed(0) : d.toFixed(2));
  }

  // Afterpay instalment = applied price / 4, always shown to 2 decimals ("$6.75", "$43.88",
  // "$27.00") to match the app's payment formatting. Rounds the split like Afterpay does.
  function fmtInstalment(cents) {
    return '$' + (Math.round(cents / 4) / 100).toFixed(2);
  }

  // ---- pair counting -------------------------------------------------------------------------

  // Layer B. Parse the server-rendered {product_id: pairs} map, once. `renderAll()` fires on every
  // widget mutation, so this must not re-parse on each pass; it re-parses only when the script's
  // text actually changes, which is what a Horizon section re-render does.
  function readPairMap() {
    var el = document.querySelector('.ydm-rp__pairmap');
    if (!el) return pairMap;
    var raw = el.textContent || '';
    if (raw === pairMapRaw && pairMap) return pairMap;
    try {
      var parsed = JSON.parse(raw);
      pairMap = (parsed && parsed.m) || {};
      pairMapRaw = raw;
    } catch (e) {
      pairMap = pairMap || {};
    }
    return pairMap;
  }

  // Layer C. One cheap request per unknown handle, memoised in sessionStorage so a second PDP view
  // resolves synchronously. The Ajax Product API returns a real `tags` array, so the same `N-pack`
  // rule the theme uses server-side can be applied here rather than guessing from the handle.
  function fetchPairs(handle) {
    if (!handle || pairPending[handle]) return;
    var key = 'ydm-pairs:' + handle;
    try {
      var cached = sessionStorage.getItem(key);
      if (cached !== null) { pairLookup[handle] = parseInt(cached, 10) || 0; return; }
    } catch (e) { /* private mode; just fetch */ }

    pairPending[handle] = true;
    fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        var pairs = 0;
        if (p && p.tags && p.tags.length) {
          for (var i = 0; i < p.tags.length; i++) {
            var m = String(p.tags[i]).toLowerCase().match(/(\d+)\s*-\s*pack\b/);
            if (m) { pairs = parseInt(m[1], 10) || 0; break; }
          }
        }
        // No pack tag. A sock single is one pair; anything else (shoehorn, fresheners, gift card)
        // is zero. `type` is unreliable across the catalogue, so a miss here stays 0 by design.
        if (!pairs && p && /sock/i.test(p.type || '')) pairs = 1;
        pairLookup[handle] = pairs;
        try { sessionStorage.setItem(key, String(pairs)); } catch (e) { /* ignore */ }
        delete pairPending[handle];
        renderAll();
      })
      .catch(function () { delete pairPending[handle]; });
  }

  // Pairs for one cart line, by the layered rule above. Returns 0 for anything unresolved, and
  // schedules the layer-C lookup so the next pass is right.
  function linePairs(item) {
    var map = readPairMap() || {};
    var byId = map[String(item.product_id)];
    if (typeof byId === 'number') return byId;
    var handle = item.handle || (item.url ? String(item.url).split('?')[0].split('/').pop() : '');
    if (Object.prototype.hasOwnProperty.call(pairLookup, handle)) return pairLookup[handle];
    fetchPairs(handle);
    return 0;
  }

  // ---- which cart pairs pool together ---------------------------------------------------------
  //
  // Kaching does not price the cart as one pile of pairs, and the bar used to assume it did. Tested
  // on live 25 September 2026: 4 pairs added as "Buy 3, Get 1" then 6 as "Buy 4, Get 2" charged $203
  // with 3 free, while the bar had promised 4 free. What Kaching actually does:
  //
  //   - a widget tier that gives free pairs is its OWN bundle, priced on its own
  //   - a fixed pack is never discounted and pools with nothing
  //   - loose singles pool together through Collection Breaks, and the widget's "1 Pair" row counts
  //     as loose (1 Pair stepped to 4 in the drawer prices at $87)
  //
  // So the bar only adds up the pairs that genuinely pool with what is being added, and takes the
  // free pairs already in the cart from the cart itself rather than predicting them.

  // Kaching bar ids that price as their own bundle, from the deal settings the widget renders on
  // the page (the ids are shared across A/B variants), then Kaching Cart's copy of the same deals.
  // null when neither is on the page, which sends lineInBundle() to its fallback.
  function ownBundleBarIds(cfg) {
    var ids = {}, found = false;
    // A percent off offer is a volume discount with Collection Breaks: every sock pair in the cart
    // counts toward the tier, whichever row added it, so no bar prices on its own.
    if (cfg && cfg.offerType === 'percent') return ids;
    var add = function (bar) {
      if (!bar || !bar.id) return;
      found = true;
      var disc = bar.discountType || (bar.discount && bar.discount.type) || 'default';
      var gets = bar.dealBarType === 'bxgy' && (parseInt(bar.getQuantity, 10) || 0) > 0;
      if (gets || (disc && disc !== 'default')) ids[bar.id] = true;
    };
    var nodes = document.querySelectorAll('script.kaching-bundles-deal-block-settings');
    for (var i = 0; i < nodes.length; i++) {
      try { (JSON.parse(nodes[i].textContent || '{}').dealBars || []).forEach(add); } catch (e) { /* skip */ }
    }
    try {
      (window.__kachingCartBundlesDeals || []).forEach(function (d) { (d.dealBars || []).forEach(add); });
    } catch (e) { /* skip */ }
    return found ? ids : null;
  }

  function lineBundle(item) {
    var raw = item && item.properties && item.properties.__kaching_bundles;
    if (!raw) return null;
    try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch (e) { return null; }
  }

  // How the cart's single pairs split: `loose` pool together, bundle pairs do not. `freeAll` and
  // `freeLoose` are pairs Kaching has actually made free, read from the $0 lines it splits them onto.
  // Before the first /cart.js read the lines are unknown, so it reports nothing pooled and nothing
  // free, the side that under-promises.
  function cartBreakdown(cfg) {
    var out = { loose: 0, freeAll: 0, freeLoose: 0, looseMaxUnit: 0 };
    if (cartItems == null) return out;
    var own = ownBundleBarIds(cfg);
    var groups = {};
    cartItems.forEach(function (it) {
      var b = lineBundle(it);
      if (b && b.id) groups[b.id] = (groups[b.id] || 0) + (it.quantity || 0);
    });
    for (var i = 0; i < cartItems.length; i++) {
      var it = cartItems[i];
      if (linePairs(it) !== 1) continue; // packs never pool; accessories carry no pairs
      var qty = it.quantity || 0;
      var free = (it.final_line_price === 0 && it.original_line_price > 0) ? qty : 0;
      var b = lineBundle(it);
      // Fallback when no deal settings are on the page: a widget bundle of more than one pair.
      var inBundle = !!(b && (own ? own[b.bid] : (b.id && groups[b.id] > 1)));
      out.freeAll += free;
      if (!inBundle) {
        out.loose += qty;
        out.freeLoose += free;
        out.looseMaxUnit = Math.max(out.looseMaxUnit, it.original_price || it.price || 0);
      }
    }
    return out;
  }

  // A widget tier that gives free pairs is priced as its own bundle.
  function isOwnBundleBar(bar, cfg) {
    if (!bar || bar.pairs == null || !cfg) return false;
    return cfg.tiers.some(function (t) { return t.v === bar.pairs && t.free > 0; });
  }

  // ---- Ka-ching widget reading -------------------------------------------------------------

  // Each tier bar row, with its title + current price. Primary path uses the confirmed
  // `.kaching-bundles__bar` structure; if that class ever drifts, fall back to resolving rows
  // from their title node so the chip still works.
  function getBars(cfg) {
    var rows = document.querySelectorAll(KC.bar);
    var bars = [];
    if (rows.length) {
      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        var t = row.querySelector(KC.title);
        var price = priceFromRow(row);
        if (t && price != null) bars.push({ row: row, title: (t.textContent || '').trim(), price: price });
      }
      if (bars.length) return withPairs(bars, cfg);
    }
    // Fallback: resolve rows from title nodes (climb to the ancestor that owns a radio).
    var titles = document.querySelectorAll(KC.title);
    var seen = [];
    for (var j = 0; j < titles.length; j++) {
      var tn = titles[j], node = tn, r = null;
      for (var up = 0; up < 6 && node; up++) {
        if (node.querySelector && node.querySelector(KC.radio)) { r = node; break; }
        node = node.parentElement;
      }
      if (!r) r = tn.closest('label') || tn.parentElement;
      if (r && seen.indexOf(r) === -1) {
        seen.push(r);
        var pr = priceFromRow(r);
        if (pr != null) bars.push({ row: r, title: (tn.textContent || '').trim(), price: pr });
      }
    }
    return withPairs(bars, cfg);
  }

  // Resolve each row's pair count once per pass. `pairs` may be null, which every consumer treats as
  // "do not project from this tier" rather than as a zero.
  //
  // Only done when a caller passes cfg. The other callers (widgetCurrentPrice, packState, the chip's
  // title re-resolve, the no-widget timeout) want a price, a title or a count, and resolving pairs
  // for them would run the cascade's DOM reads several extra times per render pass for nothing.
  function withPairs(bars, cfg) {
    if (!cfg) return bars;
    for (var i = 0; i < bars.length; i++) {
      bars[i].pairs = rungPairs(bars[i], cfg);
      bars[i].total = barTotal(bars[i], cfg);
    }
    return bars;
  }

  // A row's price as a PACK TOTAL. A/B variant B puts the per-pair price in the row's price slot
  // ($17.40 for ten pairs), and the upgrade chip used to rank and divide that as if it were the
  // total, so it offered "Go to 10 pairs, 1 pair free, $1.74/pair". Same detection as
  // totalFromWidget(): a multi-pair row priced no higher than one pair can only be a unit price.
  function barTotal(bar, cfg) {
    var unit = cfg && cfg.productPrice;
    if (!(bar.pairs > 1) || !(unit > 0) || bar.price > unit) return bar.price;
    var tier = cfg.tiers.filter(function (t) { return t.v === bar.pairs; })[0];
    if (tier && tier.pct > 0) return Math.round(tier.v * unit * (100 - tier.pct) / 100);
    if (tier && tier.v > tier.free) return (tier.v - tier.free) * unit;
    return bar.price * bar.pairs;
  }

  // Current applied (sale) price for a bar. Prefer the exact Ka-ching price node; the compare-at
  // (`.…__bar-full-price`) and per-unit (`.…__bar-subtitle`) are deliberately never read.
  function priceFromRow(row) {
    var exact = row.querySelector(KC.price);
    if (exact) {
      var pv = parseMoney(exact.textContent);
      if (pv) return pv;
    }
    // Defensive fallback for class drift: take price-ish nodes, excluding per-unit AND the
    // full/compare-at price (which is HIGHER than the sale price — a plain max() picks it wrongly).
    var nodes = row.querySelectorAll('[class*="price"], [class*="amount"], [class*="total"]');
    var bad = /compare|old|strike|was|regular|rrp|original|full/i;
    var cands = [];
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      var txt = (n.textContent || '').trim();
      if (/\/|per\b|pair|each|\bea\b/i.test(txt)) continue;
      if (bad.test(clsOf(n))) continue;
      if (n.closest('s, del, [class*="compare"], [class*="full"], [class*="strike"], [class*="old"], [class*="regular"]')) continue;
      var v = parseMoney(txt);
      if (v && v > 0) cands.push(v);
    }
    return cands.length ? Math.min.apply(null, cands) : null; // sale ≤ any stray non-compare figure
  }

  function isSelected(row) {
    if (row.matches && row.matches(KC.selected + ', [aria-checked="true"], [data-selected="true"]')) return true;
    if (/selected|active|current|checked/i.test(clsOf(row))) return true;
    var radio = row.querySelector(KC.radio);
    if (radio && radio.checked) return true;
    return false;
  }

  function selectedBar(bars) {
    for (var i = 0; i < bars.length; i++) if (isSelected(bars[i].row)) return bars[i];
    return null;
  }

  // The price the widget is CURRENTLY APPLYING (the truth source for the projection, per the
  // 2026-07-29 spec sharpening) — read straight from the selected row's price node, so it always
  // reflects what the shopper would actually pay, never a value derived from a tier label.
  function widgetCurrentPrice() {
    var el = document.querySelector(KC.selected + ' ' + KC.price);
    if (el) {
      var v = parseMoney(el.textContent);
      if (v) return v;
    }
    var bars = getBars();
    var sel = selectedBar(bars);
    return sel ? sel.price : null;
  }

  // The price of the selected tier as a PACK TOTAL, whatever the widget chooses to display.
  //
  // Kaching can be set to put the PER PAIR price in the row's main price slot instead of the pack
  // total, and Dan's 14 September config does exactly that ("$13.15 Per pair"). The three consumers
  // of this figure, the Add to cart label, the Afterpay line and the shipping line, all need the
  // total, so it is normalised here rather than in each of them.
  //
  // Detection: a tier of more than one pair whose displayed price does not exceed the single-pair
  // price can only be a unit price, since a pack total is always at least one full-price pair.
  // Under buy-N-get-M the total is exactly paid x unit price, so that is preferred over multiplying
  // the rounded per-pair figure back up, which would put $92.05 on the button for a $92 pack.
  function totalFromWidget(cfg) {
    var raw = widgetCurrentPrice();
    if (raw == null || !cfg) return raw;
    var sel = selectedBar(getBars(cfg));
    if (!sel || !(sel.pairs > 1)) return raw;
    var unit = cfg.productPrice;
    if (!(unit > 0) || raw > unit) return raw; // already a total
    var tier = cfg.tiers.filter(function (t) { return t.v === sel.pairs; })[0];
    if (tier && tier.pct > 0) return Math.round(tier.v * unit * (100 - tier.pct) / 100);
    if (tier && tier.v > tier.free) return (tier.v - tier.free) * unit;
    return raw * sel.pairs;
  }

  // How many PAIRS does this widget tier deliver?
  //
  // This is the one thing in the build that cannot be staged: the merchant configures the bundle
  // app's tier wording, the config is store-wide, and we do not know what it will say. The old
  // `packCount` parsed "5 Pack" and otherwise took the first number it found, which under BOGO is
  // catastrophically wrong in the dangerous direction: "Buy 3, Get 2 Free" returns 3, the PAID
  // count, so the bar would under-fill by two pairs on every tier.
  //
  // So: a cascade, most authoritative first, ending in null rather than a guess.
  function rungPairs(bar, cfg) {
    var title = String((bar && bar.title) || '');
    if (!title) return null;
    var t = title.toLowerCase().replace(/\s+/g, ' ').trim();

    // 1. Merchant override, always wins. A theme setting seeded with the announced offer, so it is
    //    already correct before anyone edits anything, and any surprise wording is a 30-second
    //    settings paste rather than a code push.
    if (cfg && cfg.rungMap) {
      for (var i = 0; i < cfg.rungMap.length; i++) {
        if (t.indexOf(cfg.rungMap[i].match) !== -1) return cfg.rungMap[i].pairs;
      }
    }

    // 1b. The configured tiers are the single source of truth, so "Buy 3, Get 1 Free" resolves to
    //     the total of the tier that has 3 paid pairs, whatever the rest of the wording says. This
    //     replaced the seeded map (buy 3=5, buy 4=7, buy 7=13) on 21 September 2026, because a map
    //     seeded for one offer over-counts the next one by a pair, in the dangerous direction. Only
    //     accepted when exactly one tier has that paid count.
    if (cfg && cfg.tiers) {
      var buyN = t.match(/\bbuy\D{0,4}(\d+)/);
      if (buyN) {
        var paid = parseInt(buyN[1], 10);
        var hits = cfg.tiers.filter(function (tier) { return tier.v > 0 && (tier.v - tier.free) === paid; });
        if (hits.length === 1) return hits[0].v;
      }
    }

    // 2. "get N pairs" is a TOTAL, not an addition. Checked before buy/get because the client's own
    //    best-value wording is "pay for 7, get 13 pairs", which also contains a buy-ish number. The
    //    discriminator is the word "pair" right after the number: pairs means total, free means
    //    an amount to add.
    var totalGet = t.match(/get\D{0,6}(\d+)\s*pairs?\b/);
    if (totalGet) return parseInt(totalGet[1], 10);

    // 3. Buy N get M, in any spacing or punctuation, is N + M. Not when M is a percentage: "Buy 3,
    //    Get 15% Off" is three pairs, and reading it as eighteen would promise pairs that never come.
    var bg = t.match(/buy\D{0,4}(\d+)[^0-9]{0,15}?get\D{0,4}(\d+)(?!\d|\s*%)/);
    if (bg) return parseInt(bg[1], 10) + parseInt(bg[2], 10);

    // 4. An explicit total, including the pack naming this used to rely on.
    var pairs = t.match(/(\d+)\s*(?:x\s*)?pairs?\b/);
    if (pairs) return parseInt(pairs[1], 10);
    var pack = t.match(/(\d+)\s*[- ]?\s*pack/);
    if (pack) return parseInt(pack[1], 10);

    // 4b. The bundle app's own one-pair label, which carries no digit for anything above to find.
    if (/\b(single|one pair|1 pair)\b/.test(t)) return 1;

    // 5. Infer from the money already on screen. The widget's struck price is the undiscounted value
    //    of every pair received, so dividing it by this PDP's single-pair price gives the pair count
    //    directly ($377 / $29 = 13). Only accepted when it lands close to a whole number.
    if (bar && bar.row && cfg && cfg.productPrice > 0) {
      var full = fullPriceFromRow(bar.row);
      if (full && full > cfg.productPrice) { // a per-pair compare-at cannot exceed the unit price
        var ratio = full / cfg.productPrice;
        var rounded = Math.round(ratio);
        if (rounded >= 1 && Math.abs(ratio - rounded) <= 0.15) return rounded;
      }
    }

    // 6. Unresolved. The caller suppresses the projection rather than inventing one.
    return null;
  }

  // The strikethrough on a tier row. Deliberately NOT read by priceFromRow, which wants the payable
  // price; this is the only place the compare-at is used, and only ever to infer a quantity.
  function fullPriceFromRow(row) {
    var el = row.querySelector(KC.fullPrice);
    if (el) {
      var v = parseMoney(el.textContent);
      if (v) return v;
    }
    return null;
  }

  // Parse the override setting: one `wording=pairs` rule per line, blank lines and junk ignored.
  function parseRungMap(raw) {
    var out = [];
    String(raw || '').split(/[\n,]/).forEach(function (line) {
      var m = String(line).split('=');
      if (m.length !== 2) return;
      var key = m[0].toLowerCase().replace(/\s+/g, ' ').trim();
      var n = parseInt(m[1], 10);
      if (key && n > 0) out.push({ match: key, pairs: n });
    });
    return out;
  }

  // ---- state + render ----------------------------------------------------------------------

  // One milestone per tier, measured in pairs. Each carries how many of its pairs are free and the
  // gifts it unlocks, so the copy can speak about both. Free shipping is not a member: it is earned
  // on dollars and is rendered on its own line by renderShip().
  //
  // Dan's ask (11 September) was that the bar nudge toward the next free-pair tier, not only the
  // gifts, in his own framing: "add 2 more pairs, get 1 of them free". That "1" is the MARGINAL
  // free pair, tier 2's three free minus tier 1's two, so each milestone also records the free count
  // of the tier before it.
  function milestones(cfg) {
    var out = [];
    var prevFree = 0, prevGifts = [];
    cfg.tiers.slice().sort(function (a, b) { return a.v - b.v; }).forEach(function (tier) {
      if (!(tier.v > 0)) return;
      var gifts = [];
      if (tier.gift === 'gift1' || tier.gift === 'both') gifts.push(cfg.gift1);
      if (tier.gift === 'gift2' || tier.gift === 'both') gifts.push(cfg.gift2);
      // `newGifts` is what this tier adds over the one below it, so stepping up to tier 3 says
      // "a free metal shoehorn" rather than re-listing the fresheners already earned at tier 2.
      var newGifts = gifts.filter(function (g) { return prevGifts.indexOf(g) === -1; });
      out.push({ v: tier.v, free: tier.free, pct: tier.pct || 0, gained: Math.max(0, tier.free - prevFree), gifts: gifts, newGifts: newGifts, kind: 'tier' });
      prevFree = tier.free;
      prevGifts = gifts;
    });
    return out;
  }

  function shortLabel(m) {
    return m.pct > 0 ? m.pct + '% OFF' : '+' + m.free + ' FREE';
  }

  // "3 pairs free + free shoe fresheners", cumulative for everything a tier gives you. A percent off
  // tier reads "25% off + free shoe fresheners". `extraFree` is free pairs the cart already holds
  // outside the pool, which only a free pairs offer has.
  function tierWins(m, extraFree) {
    if (m.pct > 0) return [m.pct + '% off'].concat((m.gifts || []).map(esc)).join(' + ');
    return winsText((extraFree || 0) + m.free, m.gifts);
  }

  // The same wording for a free count that is not one tier's own, e.g. a new bundle plus the free
  // pairs already sitting in the cart.
  function winsText(free, gifts) {
    var parts = [free + (free === 1 ? ' pair free' : ' pairs free')];
    return parts.concat((gifts || []).map(esc)).join(' + ');
  }

  // The highest tier a pile of `n` pooled pairs reaches, or null. Kaching rounds a part-way pile in
  // the shopper's favour (9 pairs can price as 3 free), so this can only ever under-count.
  function topReached(n, M) {
    var hit = M.filter(function (m) { return n >= m.v; });
    return hit.length ? hit[hit.length - 1] : null;
  }

  // The marginal gain of stepping up to this tier, given `n` pairs still to add. Dan's framing,
  // "get 1 of them free", is used whenever it is literally true (the free pairs gained fit inside
  // the pairs being added). Mid-tier it can stop being true: at 12 pairs, one more reaches 13 and
  // gains 3 free, and "1 more pair, get 3 of them free" is nonsense, so it falls back to a plain
  // "3 more free". Only gifts NEW at this tier are named.
  function tierGain(m, n) {
    var parts = [];
    if (m.pct > 0) {
      // A percent tier takes its percentage off every pair in the pool, so "2 more pairs, get 25%
      // off" is true whatever the cart already holds.
      parts.push('get ' + m.pct + '% off');
    } else if (m.gained > 0) {
      parts.push(m.gained <= n ? 'get ' + m.gained + ' of them free' : m.gained + ' more free');
    }
    if (m.newGifts.length) parts.push(m.newGifts.map(esc).join(' + '));
    return parts.join(' + ');
  }

  function fmtPairs(n) {
    return n + (n === 1 ? ' pair' : ' pairs');
  }

  // "2 more pairs", not "2 pairs more". The dollar version read fine as "$32 more" because the unit
  // is a prefix; pairs are a suffix, so the word order has to change with it.
  function morePairs(n) {
    return n + (n === 1 ? ' more pair' : ' more pairs');
  }

  // Fill {token} placeholders in a merchant-authored string.
  function tpl(str, vals) {
    return String(str || '').replace(/\{(\w+)\}/g, function (whole, key) {
      return Object.prototype.hasOwnProperty.call(vals, key) ? vals[key] : whole;
    });
  }

  function render(el) {
    var cfg = readCfg(el); // read fresh each time, survives Horizon section re-renders
    var M = milestones(cfg);
    var scale = cfg.maxPairs;
    var cartMoney = cartCents != null ? cartCents : cfg.cart;
    var bd = cartBreakdown(cfg);     // which cart pairs pool, and which Kaching has already made free
    var bars = getBars(cfg);
    var sel = selectedBar(bars);
    var hasWidget = bars.length > 0;

    // Once the widget has loaded and a tier selection has resolved, the chip's presence is
    // KNOWN, not pending — the async widget-load the reservation guards against has already
    // happened, so there is no CLS risk in releasing the slot. This is additive to the
    // interaction / cart-write / no-widget-timeout triggers below (it never replaces them): it
    // fixes the top-rung first-paint case — a selected tier that unlocks no upgrade, before the
    // shopper has touched anything — where the old "has the shopper interacted?" proxy left the
    // slot reserved and empty. When a chip DOES apply the slot is filled, so this is a no-op there.
    if (hasWidget && sel) collapseOk = true;

    // Let the chip's reserved slot collapse once it's safe to (see `collapseOk`).
    el.classList.toggle('ydm-rp--collapse-ok', collapseOk);

    // Truth source for the projection (2026-07-29 sharpening): the price the widget is CURRENTLY
    // APPLYING when a widget is present; otherwise the page's own product price. Never derived
    // from a tier label — if a label and the price ever disagree, the price wins.
    var widgetPrice = hasWidget ? totalFromWidget(cfg) : null;
    var currentPrice = (widgetPrice != null ? widgetPrice : cfg.productPrice) || 0;

    // The pairs this page would ADD. From the selected tier where one resolves; on a no-widget PDP
    // (a fixed pack, an accessory) from the page product's own pair count, which the block hands us.
    // `null` means the tier's wording could not be read, and every consumer below suppresses rather
    // than guesses, so the shopper never sees an invented projection.
    var addPairs = hasWidget ? (sel ? sel.pairs : null) : (cfg.productPairs || 0);
    var canProject = addPairs != null;
    // The track's unit: pairs that POOL with what this page adds, not every pair in the cart (see
    // cartBreakdown). A widget tier that gives free pairs is its own bundle and a fixed pack is never
    // discounted, so neither pools with anything; loose singles and the 1 Pair row pool together.
    // `freeElsewhere` is the free pairs the cart already holds outside that pool, so the headline
    // still describes the whole order.
    var fixedPack = !hasWidget && cfg.productPairs > 1;
    var ownBundle = hasWidget ? isOwnBundleBar(sel, cfg) : fixedPack;
    var pool = ownBundle ? 0 : bd.loose;
    var freeElsewhere = ownBundle ? bd.freeAll : Math.max(0, bd.freeAll - bd.freeLoose);
    var projected = canProject ? pool + addPairs : pool;

    // What the order would cost after the add, for the shipping and gift lines. A bundle or a pack
    // adds its own price. A loose pair can tip the loose pile into a tier, which makes a pair already
    // there free, so the order grows by less than the pair's price; estimated low, against the
    // dearest loose pair, so a gift line can never promise early.
    var addMoney = currentPrice;
    if (canProject && !ownBundle && addPairs > 0 && cfg.offerType === 'percent') {
      // Estimated low on both halves, so a gift line can never promise early: the added pairs at
      // the rate the pool reaches, and the extra taken off the pairs already in the cart priced at
      // the dearest of them.
      var pb = topReached(pool, M), pa = topReached(pool + addPairs, M);
      var pctBefore = pb ? pb.pct : 0, pctAfter = pa ? pa.pct : 0;
      var addNew = Math.round(addPairs * (cfg.productPrice || 0) * (100 - pctAfter) / 100);
      var credit = Math.round(pool * Math.max(cfg.productPrice || 0, bd.looseMaxUnit || 0) * (pctAfter - pctBefore) / 100);
      addMoney = Math.max(0, Math.min(currentPrice || addNew, addNew) - credit);
    } else if (canProject && !ownBundle && addPairs > 0) {
      var topAfter = topReached(pool + addPairs, M);
      var gained = Math.max(0, (topAfter ? topAfter.free : 0) - bd.freeLoose);
      var dearest = Math.max(cfg.productPrice || 0, bd.looseMaxUnit || 0);
      addMoney = Math.max(0, currentPrice - gained * dearest);
    }
    // A fixed pack has no widget but is still a pack; only a genuine single or an accessory is "this item".
    var unit = (hasWidget || cfg.productPairs > 1) ? 'this pack' : 'this item';
    el.toggleAttribute('data-ydm-rung-unresolved', hasWidget && sel != null && sel.pairs == null);

    var head = el.querySelector('.ydm-rp__head');
    var fill = el.querySelector('.ydm-rp__fill');
    var ghost = el.querySelector('.ydm-rp__ghost');
    var cartmark = el.querySelector('.ydm-rp__cartmark');
    var pins = el.querySelector('.ydm-rp__pins');
    var gap = el.querySelector('.ydm-rp__gap');
    var ship = el.querySelector('.ydm-rp__ship');
    var chip = el.querySelector('.ydm-rp__chip');

    var pct = function (v) { return Math.max(0, Math.min(100, (v / scale) * 100)); };

    // Track fill (pooled pairs really in the cart) + cart marker.
    fill.style.width = pct(pool) + '%';
    if (pool > 0) {
      cartmark.hidden = false;
      cartmark.style.left = pct(pool) + '%';
    } else {
      cartmark.hidden = true;
    }

    // Ghost projection segment (pool -> pool + selected tier). Suppressed entirely when the tier's
    // pair count could not be resolved, so the bar shows cart truth and nothing invented.
    if (canProject && projected > pool) {
      ghost.style.left = pct(pool) + '%';
      ghost.style.width = Math.max(0, pct(Math.min(projected, scale)) - pct(pool)) + '%';
      ghost.hidden = false;
    } else {
      ghost.hidden = true;
      ghost.style.width = '0%';
    }

    // Milestone pins.
    pins.textContent = '';
    var last = M[M.length - 1];
    M.forEach(function (m) {
      var hit = pool >= m.v;
      var proj = projected >= m.v;
      var left = pct(m.v) + '%';

      var label = document.createElement('span');
      label.className = 'ydm-rp__pinlabel' + (proj ? ' is-on' : '') + (m === last ? ' ydm-rp__pinlabel--last' : '');
      label.style.left = left;
      label.textContent = shortLabel(m);
      pins.appendChild(label);

      var pin = document.createElement('span');
      pin.className = 'ydm-rp__pin' + (hit ? ' is-hit' : (proj ? ' is-proj' : ''));
      pin.style.left = left;
      pins.appendChild(pin);

      var val = document.createElement('span');
      val.className = 'ydm-rp__pinval';
      val.style.left = left;
      // Bare numeral, not "7 pairs": at 8.5px the longer string overhangs the track's right edge
      // once the last pin sits near 100%. The unit is already carried by the headline and gap line.
      val.textContent = String(m.v);
      pins.appendChild(val);
    });

    // Drop the last (right-most) milestone label below the track when it would collide with its
    // neighbour. Measured, not breakpoint-guessed — the collision depends on the buy box's actual
    // width (narrow two-column tablet layouts collide where a full-width phone might not), and the
    // label set/text is state-independent, so this only changes with layout width, never per tier.
    dropLastLabelIfColliding(el);

    var crossed = M.filter(function (m) { return projected >= m.v; });
    var firstUnmet = M.filter(function (m) { return m.v > projected; })[0];

    // ---- headline + gap ---- Framed as "Add <this pack|this item> and you'll have …": it
    // describes the RESULTING ORDER, not the pack in isolation, so a reward part-funded by items
    // already in the cart is never miscredited to the pack alone. One phrasing is accurate with an
    // empty cart and with a part-full one, so there's no branch on cart contents. `unit` is
    // "this pack" (widget PDP) or "this item" (no-widget PDP) — never hard-code "pack".
    if (!M.length || (fixedPack && cfg.packOffTier)) {
      // No tier awards a gift, or this fixed pack is priced on no tier of the offer (a 13-pack still
      // on the last offer's price under 40% off). Nothing honest to say about tiers, so say nothing;
      // the shipping and gift lines below are dollar facts and still render.
      head.textContent = '';
      gap.textContent = '';
    } else if (!canProject) {
      // The tier's wording could not be read, so speak only about what is already in the cart.
      // Never "add this pack and you'll have", because we do not know what they'd have.
      var nextC = M.filter(function (m) { return m.v > pool; })[0];
      if (nextC) {
        head.innerHTML = 'Add <span class="ydm-rp__amt">' + morePairs(nextC.v - pool) + '</span>, ' + tierGain(nextC, nextC.v - pool);
      } else {
        head.innerHTML = '<span class="ydm-rp__win">' + tierWins(M[M.length - 1]) + '</span>, all yours';
      }
      gap.textContent = '';
    } else if (crossed.length === 0) {
      // Reaches no tier yet. Nudge to the first one, in the client's own framing: the pairs to
      // add, and how many of those come free.
      var f = M[0];
      head.innerHTML = 'Add <span class="ydm-rp__amt">' + morePairs(f.v - projected) + '</span>, ' + tierGain(f, f.v - projected);
      gap.textContent = '';
    } else {
      // The highest tier this pack reaches is what they get; everything it gives is cumulative, plus
      // any free pairs the cart already holds outside this pool.
      var top = crossed[crossed.length - 1];
      head.innerHTML = 'Add ' + unit + ' and you\'ll ' + (top.pct > 0 ? 'get' : 'have') + ' <span class="ydm-rp__win">' + tierWins(top, freeElsewhere) + '</span>';
      if (firstUnmet && fixedPack) {
        // A fixed pack cannot grow into the next tier, and loose pairs added beside it pool on
        // their own, so "4 more pairs, get 2 of them free" would be untrue here.
        gap.textContent = '';
      } else if (firstUnmet) {
        // The marginal step: "2 more pairs, get 1 of them free + free shoe fresheners".
        gap.innerHTML = '<b>' + morePairs(firstUnmet.v - projected) + '</b>, ' + tierGain(firstUnmet, firstUnmet.v - projected);
      } else if (cfg.giftMode === 'spend') {
        // Top tier reached, but in spend mode the gifts may still be short, and the gift lines below
        // are already saying so. "Nothing's left on the table" would contradict them.
        gap.textContent = '';
      } else {
        gap.textContent = 'Add ' + unit + ' and nothing\'s left on the table';
      }
    }

    // ---- free shipping, still in dollars, off the pair track ----
    renderShip(ship, cfg, cartMoney, addMoney, hasWidget);

    // ---- gifts earned at a cart value, same unit and same states as shipping ----
    renderGifts(el, cfg, cartMoney, addMoney);

    // ---- upgrade chip ----
    renderChip(chip, bars, sel, bd, M, cfg);
    clampPinValues(el);
  }

  // ---- free shipping line ---------------------------------------------------------------------
  // Free shipping is earned on cart DOLLARS while the track measures pairs, so it lives here rather
  // than as a milestone. Four states, all merchant-authored strings, and it keeps the "never speak
  // in achieved tense unless the cart genuinely holds it" rule the rest of the element follows.
  //
  // It also happens to be a useful canary: it is painted from the same widget price that feeds the
  // Afterpay line and the Add-to-cart label, so if it goes blank, those two are broken too.
  function renderShip(ship, cfg, cartMoney, currentPrice, hasWidget) {
    if (!ship) return;
    // An offer with free shipping from 0 ships everything free, so there is nothing to count down.
    if (!cfg.fs) { ship.textContent = cfg.shipAll || ''; return; }
    var threshold = fmt(cfg.fs);

    if (cartMoney >= cfg.fs) {
      ship.textContent = tpl(cfg.shipMet, { threshold: threshold, amount: '' });
      return;
    }
    if (cartMoney === 0 && !currentPrice) {
      ship.textContent = tpl(cfg.shipEmpty, { threshold: threshold, amount: fmt(cfg.fs) });
      return;
    }
    if (currentPrice && cartMoney + currentPrice >= cfg.fs) {
      ship.textContent = tpl(cfg.shipWithPack, { threshold: threshold, amount: '' });
      return;
    }
    var short = cfg.fs - (cartMoney + (currentPrice || 0));
    ship.innerHTML = esc(tpl(cfg.shipGap, { threshold: threshold, amount: fmt(short) }))
      .replace(esc(fmt(short)), '<b>' + esc(fmt(short)) + '</b>');
  }

  // ---- gift lines, spend mode -----------------------------------------------------------------
  // One row per gift, rendered by Liquid only in spend mode (so in tier mode this finds nothing and
  // does nothing). The four states mirror renderShip() exactly, with the gift's name in the copy,
  // and the same rule: "You get" only when the cart itself has crossed the figure.
  function renderGifts(el, cfg, cartMoney, currentPrice) {
    var rows = el.querySelectorAll('.ydm-rp__gift');
    if (!rows.length) return;
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var which = row.getAttribute('data-gift');
      var cents = which === '2' ? cfg.g2 : cfg.g1;
      var name = which === '2' ? cfg.gift2 : cfg.gift1;
      if (!(cents > 0) || cfg.giftMode !== 'spend') { row.textContent = ''; continue; }
      var vals = { gift: name, threshold: fmt(cents), amount: '' };
      var line;
      if (cartMoney >= cents) {
        line = tpl(cfg.giftMet, vals);
      } else if (cartMoney === 0 && !currentPrice) {
        vals.amount = fmt(cents);
        line = tpl(cfg.giftEmpty, vals);
      } else if (currentPrice && cartMoney + currentPrice >= cents) {
        line = tpl(cfg.giftWithPack, vals);
      } else {
        var short = cents - (cartMoney + (currentPrice || 0));
        vals.amount = fmt(short);
        row.innerHTML = esc(capFirst(tpl(cfg.giftGap, vals)))
          .replace(esc(fmt(short)), '<b>' + esc(fmt(short)) + '</b>');
        continue;
      }
      row.textContent = capFirst(line);
    }
  }

  // Gift names are written lower-case so they read after "unlocks" and "you get"; a line that
  // starts with one still wants a capital.
  function capFirst(s) {
    s = String(s || '');
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  // The right-most pin value can overhang the track once its pin sits near 100%. Measured, then
  // corrected with a transform only, which cannot itself cause layout shift.
  function clampPinValues(el) {
    var track = el.querySelector('.ydm-rp__track');
    var vals = el.querySelectorAll('.ydm-rp__pinval');
    if (!track || !vals.length || !el.offsetParent) return;
    var tb = track.getBoundingClientRect();
    for (var i = 0; i < vals.length; i++) {
      vals[i].style.transform = 'translateX(-50%)';
      var vb = vals[i].getBoundingClientRect();
      if (vb.right > tb.right) vals[i].style.transform = 'translateX(-100%)';
      else if (vb.left < tb.left) vals[i].style.transform = 'translateX(0)';
    }
  }

  function chipOff(chip) {
    // Keep the chip's box (reserved slot) but make it inert + invisible — constant height, zero CLS.
    chip.classList.add('is-off');
    chip.setAttribute('aria-hidden', 'true');
    chip.tabIndex = -1;
    chip.removeAttribute('data-ydm-target');
  }

  function renderChip(chip, bars, sel, bd, M, cfg) {
    // Ranked by PAIRS now, not price, since that is what the milestones measure. A tier whose
    // wording could not be resolved carries a null pair count and is simply not a candidate.
    if (!sel || sel.pairs == null || !M.length) { chipOff(chip); return; }

    // Each row reaches tiers on the pairs it pools with, same rule as the track: a free-pair tier is
    // its own bundle, the 1 Pair row joins the cart's loose pairs.
    var pooled = function (b) { return isOwnBundleBar(b, cfg) ? b.pairs : bd.loose + b.pairs; };
    var reach = function (b) { var n = pooled(b); return M.filter(function (m) { return n >= m.v; }).length; };
    var curReach = reach(sel);

    var bigger = bars.filter(function (b) {
      return b.pairs != null && b.pairs > sel.pairs && reach(b) > curReach;
    });
    if (!bigger.length) { chipOff(chip); return; }

    var target, label;
    if (curReach === 0) {
      // Selling up from a small tier: promote the CHEAPEST one that clears the first reward, which
      // is the lowest-friction step rather than the biggest jump. Ranked and divided on the pack
      // TOTAL, since variant B shows a per-pair price in the row.
      bigger.sort(function (a, b) { return a.total - b.total; });
      target = bigger[0];
      // Per-pair is the strongest number on the page under BOGO ($174 across 10 pairs is $17.40),
      // and it falls out of the pair count the tier already carries.
      var perPair = target.pairs ? ', ' + fmt(Math.round(target.total / target.pairs)) + '/pair' : '';
      label = 'Go to ' + fmtPairs(target.pairs) + ', ' + tierWins(topReached(pooled(target), M)) + perPair;
    } else {
      // Otherwise promote the tier that unlocks the MOST (cheapest on a tie).
      bigger.sort(function (a, b) { return reach(b) - reach(a) || a.total - b.total; });
      target = bigger[0];
      // "Everything unlocked" is only true when the top tier carries every gift, which is the
      // birthday sale's tier mode. In spend mode the gifts are earned on dollars (the shoehorn sits
      // above every tier), so the chip says what the tier itself gives.
      if (reach(target) === M.length && cfg.giftMode !== 'spend') {
        label = 'Go to ' + fmtPairs(target.pairs) + ', everything unlocked';
      } else {
        label = 'Go to ' + fmtPairs(target.pairs) + ', ' + tierWins(topReached(pooled(target), M));
      }
    }

    chip.innerHTML = '<span>' + esc(label) + '</span><span class="ydm-rp__chevron" aria-hidden="true">&rarr;</span>';
    chip.setAttribute('data-ydm-target', target.title); // matched back by TITLE TEXT on tap (Addendum 3.1)
    chip.classList.remove('is-off');
    chip.removeAttribute('aria-hidden');
    chip.tabIndex = 0;
  }

  // Toggle the "drop the last label below the track" treatment based on a real measurement of
  // whether the last two milestone labels overlap. `--drop-last` also reserves the track's bottom
  // margin, so the dropped label has room and the element height stays honest.
  function dropLastLabelIfColliding(el) {
    var labels = el.querySelectorAll('.ydm-rp__pinlabel');
    if (labels.length < 2) { el.classList.remove('ydm-rp--drop-last'); return; }
    if (!el.offsetParent) return; // hidden (e.g. degraded/empty-cart) — leave last decision intact
    el.classList.remove('ydm-rp--drop-last'); // measure in the inline layout
    var a = labels[labels.length - 2].getBoundingClientRect();
    var b = labels[labels.length - 1].getBoundingClientRect();
    if (b.left < a.right + 4) el.classList.add('ydm-rp--drop-last'); // 4px min clearance
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  // ---- chip tap: select the target tier in the widget (Addendum 3.1) -----------------------

  function handleChip(chip) {
    var title = chip.getAttribute('data-ydm-target');
    if (title) {
      // Re-resolve the bar by TITLE TEXT at tap time (not a stored element ref), so a widget
      // re-render between paint and tap can't leave us pointing at a stale node.
      var bar = getBars().filter(function (b) { return b.title === title; })[0];
      if (bar && bar.row) {
        var radio = bar.row.querySelector('input[type="radio"], ' + KC.radio) || bar.row;
        if (fireSelect(radio)) {
          refreshCart().then(renderAll);
          return;
        }
      }
    }
    // Graceful degradation — never a dead tap.
    var widget = document.querySelector(KC.barGuess);
    if (widget && widget.scrollIntoView) widget.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function fireSelect(node) {
    try {
      if (typeof node.click === 'function') node.click();
      // Bubbling MouseEvent fallback — some Ka-ching handlers listen on containers.
      node.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      if (node.tagName === 'INPUT') {
        node.checked = true;
        node.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return true;
    } catch (e) {
      return false;
    }
  }

  // ---- cart ---------------------------------------------------------------------------------

  function refreshCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        if (!c) return;
        if (typeof c.total_price === 'number') cartCents = c.total_price;
        // Keep the raw lines so pairs can be recounted client-side. Once this is set it supersedes
        // the server's data-cart-pairs seed, which is correct only until the cart is written to.
        if (c.items) cartItems = c.items;
      })
      .catch(function () { /* keep last known value */ });
  }

  // ---- wiring -------------------------------------------------------------------------------

  function readCfg(el) {
    var num = function (a) { return parseInt(el.getAttribute(a), 10) || 0; };
    var str = function (a, d) { return el.getAttribute(a) || d; };
    // "spend" or "tier". Liquid already blanks the tier gifts in spend mode; doing it here as well
    // means a stale data attribute can never put a gift pin on the track the offer would not honour.
    var giftMode = str('data-gift-mode', 'tier') === 'spend' ? 'spend' : 'tier';
    return {
      giftMode: giftMode,
      // "free" (Buy 3, Get 2 Free) or "percent" (Buy 3, Get 15% Off), from the live offer
      offerType: str('data-offer-type', 'free') === 'percent' ? 'percent' : 'free',
      // pairs. Each tier attribute is "total|free|gift|pct", up to four. A missing tier is 0 pairs
      // and milestones() skips it.
      tiers: ['data-t1', 'data-t2', 'data-t3', 'data-t4'].map(function (a) {
        var parts = String(el.getAttribute(a) || '').split('|');
        var gift = giftMode === 'spend' ? 'none' : (parts[2] || 'none');
        return { v: parseInt(parts[0], 10) || 0, free: parseInt(parts[1], 10) || 0, gift: gift, pct: parseInt(parts[3], 10) || 0 };
      }),
      // dollars (cents) that earn each gift in spend mode. 0 means that gift is off.
      g1: num('data-g1'),
      g2: num('data-g2'),
      maxPairs: num('data-max-pairs') || 15,
      productPairs: num('data-product-pairs'),
      packOffTier: el.hasAttribute('data-pack-off-tier'),
      // dollars (cents), for the shipping line and the price-derived rung inference
      fs: num('data-fs'),
      cart: num('data-cart'),
      productPrice: num('data-product-price'),
      // copy
      ship: str('data-ship', 'free shipping'),
      shipAll: str('data-ship-all', ''),
      gift1: str('data-gift1', 'free shoe fresheners'),
      gift2: str('data-gift2', 'a free metal shoehorn'),
      shipEmpty: str('data-ship-empty', 'Free shipping over {threshold}'),
      shipGap: str('data-ship-gap', '{amount} more for free shipping'),
      shipWithPack: str('data-ship-with-pack', 'Add this pack and shipping is free'),
      shipMet: str('data-ship-met', 'Free shipping, you are there'),
      giftEmpty: str('data-gift-empty', '{gift} over {threshold}'),
      giftGap: str('data-gift-gap', '{amount} more for {gift}'),
      giftWithPack: str('data-gift-with-pack', 'Add this pack and you get {gift}'),
      giftMet: str('data-gift-met', 'You get {gift}'),
      rungMap: parseRungMap(el.getAttribute('data-rung-map'))
    };
  }

  // ---- wiring — all listeners live at MODULE scope, bound once, so Horizon section
  // re-renders (variant changes replace the block DOM) can't stack listeners or leave a
  // fresh block un-hydrated. Every trigger just calls renderAll() over whatever is in the DOM.

  function renderAll() {
    var els = document.querySelectorAll('[data-ydm-reward-progress]');
    for (var i = 0; i < els.length; i++) render(els[i]);
    // Read the widget ONCE per pass and hand the same answer to every consumer that paints a
    // widget-derived figure, so the Afterpay line and the buy button can never disagree with
    // each other (or with the bar) about what the selected pack costs.
    var state = packState();
    renderAfterpay(state);
    renderAtcPrice(state);
  }

  // The single answer to "does this PDP have pack rungs, and what is the widget applying right
  // now". `hasPacks` and `price` are DIFFERENT predicates and must stay separate: the bundle app
  // injects an empty <kaching-bundle> on preset-pack PDPs (so presence is not a build-your-own
  // signal), and on a real build-your-own the rungs exist for a moment before a price is readable.
  function packState() {
    var hasPacks = getBars().length > 0; // real pack rungs => build-your-own (not an empty widget)
    var first = document.querySelector('[data-ydm-reward-progress]');
    var cfg = first ? readCfg(first) : null;
    return { hasPacks: hasPacks, price: hasPacks ? totalFromWidget(cfg) : null }; // cents, or null
  }

  // Paint the custom Afterpay instalment line(s) from the SAME widget-price reader the bar
  // uses — no second reader, no second observer.
  //
  // Gating: our line replaces the app's Afterpay message ONLY where the bundle widget actually
  // has pack RUNGS — i.e. a build-your-own PDP — decided here in JS, not by CSS `:has(> kaching-
  // bundle)`. The bundle app injects an EMPTY `<kaching-bundle>` on preset-pack PDPs too, so keying
  // the swap on the widget's mere presence would wrongly hide the preset-pack's correct app line and
  // leave a blank gap. We toggle `.ydm-has-packs` on the buy box only when real bars are present; the
  // buy-box CSS keys the hide-app/show-ours swap off that class. While the widget's price is not yet
  // readable we also keep the line hidden rather than fall back to the single-pair product price.
  function renderAfterpay(state) {
    var aps = document.querySelectorAll('[data-ydm-afterpay]');
    if (!aps.length) return;
    for (var i = 0; i < aps.length; i++) {
      var el = aps[i];
      var box = el.closest('.layout-panel-flex');
      if (box) box.classList.toggle('ydm-has-packs', state.hasPacks);
      if (state.price == null) { el.classList.remove('ydm-ap--ready'); continue; }
      var amt = el.querySelector('.ydm-ap__amt');
      if (amt) amt.textContent = fmtInstalment(state.price);
      el.classList.add('ydm-ap--ready');
    }
  }

  // Paint the live pack price onto the Add-to-cart label ("Add to cart · $170") from that same
  // read — a third consumer of one reader, never a second price source.
  //
  // Formatting is `fmt()`, the whole-amount formatter the reward bar's thresholds and gap line
  // already use ($170, and $175.50 only where there are cents) — not the Afterpay line's
  // `fmtInstalment()`, which is fixed to 2dp because a quarter-share of a price genuinely has
  // cents. The two agree on the figure; each renders it in the house style for what it is.
  //
  // Where no pack price resolves the amount is CLEARED, not merely hidden, so the button's
  // accessible name can never announce a stale figure — and the label falls back to plain "Add
  // to cart" rather than the page's single-pair product price, which on a build-your-own PDP
  // would be a wrong number on the primary purchase control.
  function renderAtcPrice(state) {
    var els = document.querySelectorAll('[data-ydm-atc-price]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var amt = amtTextNode(el);
      if (state.price == null) {
        amt.nodeValue = '';
        el.classList.remove('is-ready');
        continue;
      }
      amt.nodeValue = fmt(state.price);
      el.classList.add('is-ready');
    }
  }

  // The amount is a BARE TEXT NODE after the separator element, never an element of its own —
  // see the long note in `snippets/add-to-cart-button.liquid`. In short: the bundle app claims
  // the first element inside the buy button whose first child is number-ish text and rewrites
  // it in its own money format, and this shape is what keeps our figure ours. Created on demand
  // so a section re-render (which restores the server markup, separator only) re-hydrates.
  function amtTextNode(el) {
    var last = el.lastChild;
    if (last && last.nodeType === 3) return last;
    return el.appendChild(document.createTextNode(''));
  }

  var widgetObserver = null;
  function attachWidgetObserver() {
    if (widgetObserver) return;
    var w = document.querySelector(KC.barGuess);
    if (!w) return;
    // Re-read on the widget's OWN mutations — the tier's selected class flip AND the price
    // node settling to its new value (characterData) — rather than on a fixed post-click timer.
    // The debounce coalesces the burst the widget emits mid-update and fires once it has settled,
    // so the projection reads the final price instead of an intermediate one.
    widgetObserver = new MutationObserver(debounce(renderAll, 80));
    widgetObserver.observe(w.closest('[class*="kaching-bundles"]') || w, {
      attributes: true, subtree: true, childList: true, characterData: true,
      attributeFilter: ['class', 'aria-checked', 'checked', 'value']
    });
    renderAll();
  }

  // ---- app-agnostic cart-write interception ------------------------------------------------
  // The Ka-ching drawer (and any other cart app/theme) mutates the cart through the standard
  // Shopify cart WRITE endpoints. Rather than enumerate every app's custom event names, we watch
  // for the network calls any cart mutation must make and re-evaluate once they resolve. This is
  // additive to the named `cart:*` listeners below and is wrapped in try/catch so that, if
  // primitive-wrapping ever fails, the bar keeps working on those listeners exactly as today.
  //
  // Matches /cart/add, /cart/change, /cart/update, /cart/clear — with or without a `.js` suffix,
  // and allowing a locale prefix (e.g. /en-au/cart/change.js) or a query string. CRITICAL: it must
  // NOT match the /cart.js READ that refreshCart() itself performs, or every refresh would trigger
  // another refresh in an infinite loop — `/cart/<verb>` requires a slash after `cart`, so the
  // `/cart.js` read (a dot after `cart`) never matches.
  var CART_WRITE_RE = /\/cart\/(?:add|change|update|clear)(?:\.js)?(?:[/?#]|$)/i;

  function isCartWrite(url) {
    if (!url) return false;
    try { return CART_WRITE_RE.test(String(url)); } catch (e) { return false; }
  }

  // Debounced so a burst of writes (e.g. clearing several lines) coalesces into one /cart.js read.
  var scheduleCartRefresh = debounce(function () { refreshCart().then(renderAll); }, 120);

  function onCartWrite() {
    // A drawer cart mutation is a user-initiated change, so the chip's reserved slot may collapse.
    collapseOk = true;
    scheduleCartRefresh();
  }

  var cartWriteInstalled = false;
  function installCartWriteInterception() {
    if (cartWriteInstalled) return;
    cartWriteInstalled = true;

    // fetch — chain, never clobber. Capture the previous implementation (another app may already
    // have patched it), call through it preserving `this`, and return its exact promise so the
    // app's own error handling sees the result/rejection unchanged. Our reaction lives on a
    // separate derived promise that we never surface.
    try {
      if (typeof window.fetch === 'function') {
        var prevFetch = window.fetch;
        window.fetch = function (input, init) {
          var url = '';
          try { url = (input && typeof input === 'object' && input.url) ? input.url : String(input); } catch (e) { url = ''; }
          var ret = prevFetch.apply(this, arguments);
          try {
            if (isCartWrite(url) && ret && typeof ret.then === 'function') {
              // React only after a SUCCESSFUL write; the no-op rejection handler is on OUR branch
              // only and does not affect the original promise `ret` returned to the caller.
              ret.then(onCartWrite, function () {});
            }
          } catch (e) { /* never let our hook break the app's fetch */ }
          return ret;
        };
      }
    } catch (e) { /* fall back to the event listeners */ }

    // XMLHttpRequest — same principle. Flag the request at open() time by URL, then fire once it
    // has finished (loadend fires on success AND error; refreshCart is cheap and idempotent, and
    // a failed write simply reads back the unchanged cart).
    try {
      var XHR = window.XMLHttpRequest;
      if (XHR && XHR.prototype && XHR.prototype.open && XHR.prototype.send) {
        var prevOpen = XHR.prototype.open;
        var prevSend = XHR.prototype.send;
        XHR.prototype.open = function (method, url) {
          try { this.__ydmCartWrite = isCartWrite(url); } catch (e) { this.__ydmCartWrite = false; }
          return prevOpen.apply(this, arguments);
        };
        XHR.prototype.send = function () {
          try {
            if (this.__ydmCartWrite) this.addEventListener('loadend', onCartWrite);
          } catch (e) { /* leave the send untouched */ }
          return prevSend.apply(this, arguments);
        };
      }
    } catch (e) { /* fall back to the event listeners */ }
  }

  var bound = false;
  function bindGlobal() {
    if (bound) return;
    bound = true;

    // Install the network interception first so it is live before any drawer interaction.
    installCartWriteInterception();

    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      var chip = e.target.closest('.ydm-rp__chip');
      if (chip) { collapseOk = true; handleChip(chip); return; }
      // A tap on a widget tier row changes the selection. The widget observer drives the
      // re-read off the price node settling; this timer is only a backstop. Either way it's a
      // user-initiated change, so the chip's slot may now collapse in no-upgrade states.
      if (e.target.closest(KC.barGuess)) { collapseOk = true; setTimeout(renderAll, 120); }
    }, true);

    ['cart:updated', 'cart:refresh', 'cart:change', 'variant:added', 'ajaxProduct:added', 'product:added']
      .forEach(function (name) {
        document.addEventListener(name, function () { collapseOk = true; refreshCart().then(renderAll); });
      });

    // One body observer catches late widget injection (app-embed loads async) AND a section
    // re-render dropping in a fresh block. Debounced so a busy PDP stays cheap.
    var react = debounce(function () { attachWidgetObserver(); renderAll(); }, 100);
    new MutationObserver(react).observe(document.body, { childList: true, subtree: true });
    [300, 800, 1500, 3000, 6000].forEach(function (t) { setTimeout(attachWidgetObserver, t); });

    // No bundle widget after the app-embed has had time to inject => a no-widget PDP (fixed pack /
    // accessory): the upgrade chip can never appear here, so release its reserved slot rather than
    // leave the box padded with dead space. 3.5s clears the embed's normal load window; a widget
    // slower than that stays correct (it just animates the chip in via the transition) — rare, and
    // the smooth growth reads as a settle, not a jump.
    setTimeout(function () {
      if (!document.querySelector(KC.barGuess) && !getBars().length) { collapseOk = true; renderAll(); }
    }, 3500);
  }

  function debounce(fn, ms) {
    var t;
    return function () { clearTimeout(t); t = setTimeout(fn, ms); };
  }

  function boot() {
    var first = document.querySelector('[data-ydm-reward-progress]');
    if (cartCents == null && first) cartCents = parseInt(first.getAttribute('data-cart'), 10) || 0;
    bindGlobal();
    attachWidgetObserver();
    renderAll();
    refreshCart().then(renderAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
